import React, { useState, useContext, useRef, useCallback, useEffect } from 'react';
import T from '../../components/common/T';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig'; 
import { toast } from "react-toastify";

import { 
  SwatchIcon, 
  GlobeAltIcon, 
  CurrencyDollarIcon, 
  ShieldCheckIcon,
  ComputerDesktopIcon,
  UserGroupIcon, 
  PaperAirplaneIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  SquaresPlusIcon,
  BriefcaseIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  ChartPieIcon,
  BanknotesIcon,
  BuildingOffice2Icon, PlusIcon, TrashIcon, ArrowPathIcon,
  Squares2X2Icon, ClockIcon,       
  CalendarIcon,
  BellIcon // 🟢 ADDED THIS
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid'; 
import { SettingsContext } from '../../context/SettingsContext';
import { CurrencyContext } from '../../context/CurrencyContext';
import SMTPSettings from './SMTPSettings';
import CustomFieldSettings from './CustomFieldSettings';
import { useCurrency } from '../../hooks/useCurrency';

// --- MODULE SETTINGS ---
const ModuleSettings = () => {
  const { activeModules, saveSettings } = useContext(SettingsContext);

  const availableModules = [
    { id: 'crm', name: 'CRM & Leads', desc: 'Track inquiries, site visits, and sales pipelines.', icon: UserGroupIcon, color: 'text-purple-600', bg: 'bg-purple-100' },
    { id: 'projects', name: 'Projects', desc: 'Manage project lifecycles and timelines.', icon: BriefcaseIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
    { id: 'tasks', name: 'Tasks & Kanban', desc: 'Work orders, tasks, and timesheets.', icon: ClipboardDocumentListIcon, color: 'text-sky-600', bg: 'bg-sky-100' },
    { id: 'accounting', name: 'Finance & Accounts', desc: 'Budgets, client invoices, and bills.', icon: BanknotesIcon, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { id: 'materials', name: 'Materials', desc: 'Purchase orders, internal picking, and scrap.', icon: TruckIcon, color: 'text-orange-600', bg: 'bg-orange-100' },
    { id: 'vendor', name: 'Vendors', desc: 'Manage suppliers and portal access.', icon: BuildingOffice2Icon, color: 'text-amber-600', bg: 'bg-amber-100' },
    { id: 'inventory', name: 'Inventory', desc: 'Track stock and material issues.', icon: BuildingOffice2Icon, color: 'text-indigo-600', bg: 'bg-indigo-100' },
    { id: 'warehouse', name: 'Warehouse', desc: 'Manage multiple storage locations.', icon: BuildingOffice2Icon, color: 'text-cyan-600', bg: 'bg-cyan-100' },
    { id: 'workforce', name: 'Workforce', desc: 'Allocations and performance tracking.', icon: UserGroupIcon, color: 'text-teal-600', bg: 'bg-teal-100' },
    { id: 'employee', name: 'Employees (HR)', desc: 'Core HR profiles and leave management.', icon: UserGroupIcon, color: 'text-pink-600', bg: 'bg-pink-100' },
    { id: 'assets', name: 'Assets & Equipment', desc: 'Manage heavy machinery and maintenance.', icon: WrenchScrewdriverIcon, color: 'text-rose-600', bg: 'bg-rose-100' },
    { id: 'documents', name: 'Documents', desc: 'Centralized file storage and sharing.', icon: ClipboardDocumentListIcon, color: 'text-gray-600', bg: 'bg-gray-100' },
    { id: 'compliance', name: 'Compliance & Safety', desc: 'Track site safety and certificates.', icon: ShieldCheckIcon, color: 'text-green-600', bg: 'bg-green-100' },
    { id: 'reporting', name: 'Reporting & Analytics', desc: 'Advanced system-wide analytics.', icon: ChartPieIcon, color: 'text-fuchsia-600', bg: 'bg-fuchsia-100' },
  ];

  const handleToggle = (moduleId) => {
    const newModules = { ...activeModules, [moduleId]: !activeModules[moduleId] };
    saveSettings({ activeModules: newModules });
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 animate-fade-in-up">
      <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <SquaresPlusIcon className="w-6 h-6 text-blue-600"/> App Integrations & Modules
            </h3>
            <p className="text-sm text-gray-500 mt-1"><T>Enable or disable core system modules. Disabled modules will hide their UI and menus system-wide.</T></p>
          </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {availableModules.map((mod) => {
          const isEnabled = activeModules[mod.id] !== false; // Default true if undefined
          
          return (
            <div 
              key={mod.id} 
              className={`relative flex flex-col p-5 rounded-xl border-2 transition-all duration-300 ${
                isEnabled ? 'border-gray-200 bg-white shadow-sm hover:border-blue-300' : 'border-gray-100 bg-gray-50/50 opacity-75 grayscale-[30%]'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className={`p-2.5 rounded-lg ${isEnabled ? mod.bg : 'bg-gray-200'} ${isEnabled ? mod.color : 'text-gray-500'} transition-colors`}>
                  <mod.icon className="w-6 h-6" />
                </div>
                
                {/* Tailwind Custom Toggle Switch */}
                <button
                  type="button"
                  onClick={() => handleToggle(mod.id)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    isEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                  }`}
                >
                  <span className="sr-only">Use setting</span>
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div>
                <h4 className={`text-base font-bold mb-1 ${isEnabled ? 'text-gray-900' : 'text-gray-600'}`}>{mod.name}</h4>
                <p className="text-xs text-gray-500 leading-relaxed min-h-[40px]">{mod.desc}</p>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                 <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                     isEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-200 text-gray-500'
                 }`}>
                     {isEnabled ? 'Installed' : 'Disabled'}
                 </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- 1. FONT SETTINGS ---
const FontSettings = () => {
  const { font, saveSettings } = useContext(SettingsContext);
  
  const fontOptions = [
    { name: 'Inter', label: 'Default Modern UI', category: 'Sans-Serif' },
    { name: 'Roboto', label: 'Standard Android', category: 'Sans-Serif' },
    { name: 'Open Sans', label: 'Neutral & Clean', category: 'Sans-Serif' },
    { name: 'Lato', label: 'Friendly & Corporate', category: 'Sans-Serif' },
    { name: 'Poppins', label: 'Geometric', category: 'Sans-Serif' },
    { name: 'Montserrat', label: 'Wide & Bold', category: 'Sans-Serif' },
    { name: 'Playfair Display', label: 'Elegant Editorial', category: 'Serif' },
    { name: 'Merriweather', label: 'Highly Readable', category: 'Serif' },
    { name: 'Roboto Mono', label: 'Developer Style', category: 'Monospace' },
    { name: 'Oswald', label: 'Tall & Bold', category: 'Display' },
    { name: 'Raleway', label: 'Thin & Artistic', category: 'Sans-Serif' },
    { name: 'Nunito', label: 'Rounded & Soft', category: 'Sans-Serif' }
  ];

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6">
      <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
        <span className="text-2xl font-serif italic"><T>Aa</T></span> Typography
      </h3>
      <p className="text-sm text-gray-500 mb-6"><T>Select a font to instantly apply it across the entire ERP system.</T></p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {fontOptions.map((option) => (
          <button
            key={option.name}
            onClick={() => saveSettings({ font: option.name })}
            className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-md group ${
              font === option.name 
                ? 'border-indigo-600 bg-indigo-50 ring-1 ring-indigo-600' 
                : 'border-gray-200 hover:border-indigo-300 bg-white'
            }`}
          >
            <div style={{ fontFamily: option.name }} className="mb-3 border-b border-gray-100 pb-2">
              <span className="text-2xl font-normal text-gray-900"><T>Ag</T></span>
              <span className="text-lg ml-2">123</span>
              <p className="text-xs mt-1 truncate"><T>The quick brown fox jumps.</T></p>
            </div>
            <div className="flex justify-between items-end">
                <div>
                    <div className="text-sm font-bold text-gray-900">{option.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{option.category}</div>
                </div>
            </div>
            {font === option.name && (
              <CheckCircleIcon className="w-5 h-5 text-indigo-600 absolute top-3 right-3" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// --- 2. THEME SETTINGS ---
const ThemeSettings = () => {
  const { theme, saveSettings } = useContext(SettingsContext);
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
        <SwatchIcon className="w-5 h-5 text-indigo-600"/> Appearance
      </h3>
      <div className="grid grid-cols-3 gap-4">
        {['light', 'dark', 'system'].map((mode) => (
          <button
            key={mode}
            onClick={() => saveSettings({ theme: mode })}
            className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 capitalize transition-all ${
              theme === mode 
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm' 
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
            }`}
          >
            <ComputerDesktopIcon className="w-6 h-6"/>
            <span className="font-medium">{mode}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const CurrencySettings = () => {
  const { activeCurrency, setActiveCurrency, availableCurrencies, formatMoney, fetchCurrencies } = useCurrency();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshRates = async () => {
    setIsRefreshing(true);
    try {
      await api.get('/settings/currencies/rates');
      await fetchCurrencies(); // Reload context state
    } catch (error) {
      console.error(error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <CurrencyDollarIcon className="w-5 h-5 text-green-600"/> <T>Currency Configuration</T>
        </h3>
        <button 
            onClick={handleRefreshRates}
            disabled={isRefreshing}
            className="flex items-center gap-2 text-xs font-bold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200 hover:bg-green-100 transition disabled:opacity-50"
        >
            <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? <T>Updating...</T> : <T>Refresh Live Rates</T>}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2"><T>System Currency</T></label>
          <select 
            value={activeCurrency?.id || ''}
            onChange={(e) => {
                const selected = availableCurrencies.find(c => Number(c.id) === Number(e.target.value));
                if(selected) setActiveCurrency(selected); 
            }}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
          >
            {availableCurrencies.map(c => (
              <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-2">
            <T>Last Updated:</T> {activeCurrency?.last_updated ? new Date(activeCurrency.last_updated).toLocaleString() : 'Never'}
          </p>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col justify-center items-center">
          <span className="text-sm text-gray-500 uppercase font-bold tracking-wider"><T>Live Preview</T></span>
          
          <span className="text-3xl font-bold text-gray-900 mt-2 font-mono">
            {formatMoney(1000)}
          </span>
          
          <span className="text-xs text-gray-400 mt-1">
            <T>Exchange Rate:</T> 1 USD = {activeCurrency?.exchange_rate} {activeCurrency?.code}
          </span>
        </div>
      </div>
    </div>
  );
};

const LanguageSettings = () => {
  const { language, availableLanguages, saveSettings } = useContext(SettingsContext);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const VALID_ADDABLE_LANGUAGES = [
    { code: 'it',    name: 'Italian',              flag: '🇮🇹' },
    { code: 'pt',    name: 'Portuguese',           flag: '🇵🇹' },
    { code: 'ru',    name: 'Russian',              flag: '🇷🇺' },
    { code: 'ja',    name: 'Japanese',             flag: '🇯🇵' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
    { code: 'ar',    name: 'Arabic',               flag: '🇸🇦' },
    { code: 'ko',    name: 'Korean',               flag: '🇰🇷' },
    { code: 'tr',    name: 'Turkish',              flag: '🇹🇷' },
  ];

  const triggerTranslation = useCallback((code) => {
    try {
      if (typeof window.changeLanguage === 'function') {
        window.changeLanguage(code);
      }
    } catch (err) {
      console.warn('Translation error (non-fatal):', err);
    }
  }, []);

  const handleLanguageChange = useCallback((code) => {
    saveSettings({ language: code });
    triggerTranslation(code);
  }, [saveSettings, triggerTranslation]);

  const handleAddLanguage = useCallback((newLang) => {
    if (availableLanguages.some(l => l.code === newLang.code)) return;
    const updatedList = [...availableLanguages, newLang];
    saveSettings({ availableLanguages: updatedList });
    setShowAddMenu(false);
  }, [availableLanguages, saveSettings]);

  const handleRemoveLanguage = useCallback((codeToRemove, e) => {
    e.stopPropagation();
    if (codeToRemove === 'en') return;
    const updatedList = availableLanguages.filter(l => l.code !== codeToRemove);
    const patch = { availableLanguages: updatedList };
    if (language === codeToRemove) {
      patch.language = 'en';
      triggerTranslation('en');
    }
    saveSettings(patch);
  }, [availableLanguages, language, saveSettings, triggerTranslation]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <GlobeAltIcon className="w-5 h-5 text-blue-600" />
          <T>Language & Region</T>
        </h3>
        <button
          onClick={() => setShowAddMenu(prev => !prev)}
          className="flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition"
        >
          <PlusIcon className="w-4 h-4" />
          <T>Add Language</T>
        </button>
      </div>

      <div className="space-y-4">
        {showAddMenu && (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
              <T>Select a new language to support</T>
            </label>
            <div className="flex gap-2 flex-wrap">
              {VALID_ADDABLE_LANGUAGES.map(lang => {
                const isAlreadyAdded = availableLanguages.some(l => l.code === lang.code);
                if (isAlreadyAdded) return null;
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleAddLanguage(lang)}
                    className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2 rounded-lg hover:border-blue-500 hover:text-blue-600 transition text-sm"
                  >
                    <span className="notranslate" translate="no">{lang.flag}</span>
                    <span className="notranslate" translate="no">{lang.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <T>Display Language</T>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {availableLanguages.map(lang => (
              <div key={lang.code} className="relative group">
                <button
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    language === lang.code
                      ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <span
                    className="notranslate"
                    translate="no"
                    aria-hidden="true"
                    style={{ fontSize: '1.5rem', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}
                  >
                    {lang.flag}
                  </span>
                  <span
                    className="notranslate font-medium text-gray-700 text-sm flex-1"
                    translate="no"
                  >
                    {lang.name}
                  </span>
                  {language === lang.code && (
                    <CheckCircleIcon className="w-5 h-5 text-blue-600 shrink-0" />
                  )}
                </button>

                {lang.code !== 'en' && (
                  <button
                    onClick={(e) => handleRemoveLanguage(lang.code, e)}
                    className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full p-1 shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 hover:bg-red-50 transition"
                    title="Remove Language"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
            <CheckCircleIcon className="w-3 h-3 text-green-500" />
            <T>Intelligent DOM Translation Active. Placeholders, Inputs, and Alerts will translate automatically.</T>
          </p>
        </div>
      </div>
    </div>
  );
};

// --- 5. HISTORY LOGS ---
const HistoryLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
           setError("No authentication token found. Please login again.");
           setLoading(false);
           return;
        }

        const response = await api.get('/users/audit-logs');

        if (Array.isArray(response.data)) {
            setLogs(response.data);
            setError(null);
        } else {
            setLogs([]);
            setError("Invalid data format received from server.");
        }

      } catch (err) {
        setError(err.response?.data?.message || "Failed to load logs. Check server connection.");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const getActionColor = (action) => {
    switch(action?.toLowerCase()) {
      case 'delete': return 'bg-red-100 text-red-700 border-red-200';
      case 'create': return 'bg-green-100 text-green-700 border-green-200';
      case 'update': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'login': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'logout': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  return (
<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-fade-in-up">
  <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
    <div>
      <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
        <ClipboardDocumentListIcon className="w-5 h-5 text-gray-600"/>
        System Audit Logs
      </h3>
    </div>
    <button onClick={() => window.location.reload()} className="text-xs text-emerald-600 font-bold hover:text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded transition">Refresh</button>
  </div>
  
  <div className="w-full">
    {loading ? (
      <div className="p-8 text-center text-gray-500 flex flex-col items-center gap-2">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm">Loading...</span>
      </div>
    ) : error ? (
       <div className="p-8 text-center text-red-500 text-sm">{error}</div>
    ) : logs.length === 0 ? (
      <div className="p-8 text-center text-gray-500 text-sm">No activity recorded yet.</div>
    ) : (
      <table className="w-full table-fixed text-left border-collapse">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-bold">
          <tr>
            <th className="px-5 py-3 border-b border-gray-100 w-[30%]">User & Time</th>
            <th className="px-5 py-3 border-b border-gray-100 w-[25%]">Action Context</th>
            <th className="px-5 py-3 border-b border-gray-100 w-[45%]">Change Detail</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 text-sm">
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-gray-50/50 transition group">
              <td className="px-5 py-3 align-top">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-800 text-sm truncate">{log.user_name || "System"}</span>
                  <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">{log.user_role || "N/A"}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(log.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </td>
              <td className="px-5 py-3 align-top">
                <div className="flex flex-col items-start gap-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wide ${getActionColor(log.action)}`}>{log.action}</span>
                  <span className="text-xs font-medium text-gray-500">in <span className="text-gray-700">{log.module}</span></span>
                </div>
              </td>
              <td className="px-5 py-3 align-top">
                <div className="text-sm text-gray-600">
                  {log.old_value && log.old_value !== 'null' ? (
                    <div className="grid grid-cols-1 gap-0.5 text-xs">
                       <div className="flex items-center gap-1 text-red-400">
                         <span className="w-8 uppercase text-[10px] font-bold opacity-70">From:</span>
                         <span className="truncate max-w-[200px] line-through decoration-red-200">{log.old_value}</span>
                       </div>
                       <div className="flex items-center gap-1 text-emerald-600">
                          <span className="w-8 uppercase text-[10px] font-bold opacity-70">To:</span>
                          <span className="font-medium truncate max-w-[200px]">{log.new_value}</span>
                       </div>
                    </div>
                  ) : (
                    <p className="truncate text-gray-500 w-full" title={log.description || `Record ID: ${log.record_id}`}>
                      {log.description || `Modified Record ID: ${log.record_id}`}
                    </p>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
</div>
  );
};

// --- HR & ATTENDANCE SETTINGS ---
const HRSettingsTab = () => {
  const [hrSettings, setHrSettings] = useState({ 
      time_format: '12h', 
      geofence_enabled: true, 
      grace_period_mins: 15,
      weekend_days: 'Sunday',
      half_day_hours: 4.0,
      payroll_cycle_start: 1,
      // 🟢 NEW INITIAL STATE
      default_casual_leaves: 12,
      default_sick_leaves: 12,
      default_earned_leaves: 15,
      max_leaves_per_month: 3
  });
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ holiday_date: '', holiday_name: '', is_recurring: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      try {
        const [hrRes, holRes] = await Promise.all([
          api.get('/settings/hr'),
          api.get('/settings/hr/holidays')
        ]);
        setHrSettings({
          ...hrRes.data,
          geofence_enabled: Boolean(hrRes.data.geofence_enabled)
        });
        setHolidays(holRes.data);
      } catch (err) {
        console.error("Failed to load HR settings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSaveConfig = async () => {
    try {
      await api.post('/settings/hr', hrSettings);
      toast.success("HR Settings Saved Successfully!");
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    try {
      await api.post('/settings/hr/holidays', newHoliday);
      const holRes = await api.get('/settings/hr/holidays');
      setHolidays(holRes.data);
      setNewHoliday({ holiday_date: '', holiday_name: '', is_recurring: false });
      toast.success("Holiday added successfully!");
    } catch (err) {
      toast.error("Failed to add holiday");
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      await api.delete(`/settings/hr/holidays/${id}`);
      setHolidays(holidays.filter(h => h.id !== id));
      toast.success("Holiday deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete holiday");
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading HR Settings...</div>;

  return (
    <div className="space-y-6 animate-fade-in-up">
      
      {/* 🟢 NEW: LEAVE & QUOTA CONFIGURATION */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-purple-500">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-purple-600"/> Annual Leave Quotas & Limits
        </h3>
        <p className="text-xs text-gray-500 mb-6">Define the default paid leave balances given to new employees every year. Unpaid leaves are deducted automatically from payroll if these balances reach zero.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-2">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Casual Leaves</label>
            <input type="number" min="0" value={hrSettings.default_casual_leaves || 0} onChange={(e) => setHrSettings({...hrSettings, default_casual_leaves: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Sick Leaves</label>
            <input type="number" min="0" value={hrSettings.default_sick_leaves || 0} onChange={(e) => setHrSettings({...hrSettings, default_sick_leaves: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Earned Leaves</label>
            <input type="number" min="0" value={hrSettings.default_earned_leaves || 0} onChange={(e) => setHrSettings({...hrSettings, default_earned_leaves: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" />
          </div>
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
            <label className="block text-xs font-bold text-purple-700 uppercase mb-2">Max per Month</label>
            <input type="number" min="0" value={hrSettings.max_leaves_per_month || 0} onChange={(e) => setHrSettings({...hrSettings, max_leaves_per_month: e.target.value})} className="w-full p-2 border border-purple-200 rounded-md focus:ring-2 focus:ring-purple-500 outline-none bg-white text-purple-900 font-bold" />
          </div>
        </div>
      </div>

      {/* GLOBAL TIME CONFIG */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-blue-500">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-blue-600"/> Global Attendance Rules
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Time Format</label>
            <select value={hrSettings.time_format} onChange={(e) => setHrSettings({...hrSettings, time_format: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 outline-none">
              <option value="12h">12-Hour (AM/PM)</option>
              <option value="24h">24-Hour (Military)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Late Grace Period (Mins)</label>
            <input type="number" value={hrSettings.grace_period_mins} onChange={(e) => setHrSettings({...hrSettings, grace_period_mins: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Half-Day Threshold (Hours)</label>
            <input type="number" step="0.5" value={hrSettings.half_day_hours} onChange={(e) => setHrSettings({...hrSettings, half_day_hours: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Weekly Off (Weekends)</label>
            <select value={hrSettings.weekend_days} onChange={(e) => setHrSettings({...hrSettings, weekend_days: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 outline-none">
              <option value="Sunday">Sunday Only</option>
              <option value="Saturday, Sunday">Saturday & Sunday</option>
              <option value="Friday">Friday Only (Middle East)</option>
            </select>
          </div>
          <div>
             <label className="block text-sm font-bold text-gray-700 mb-2">Payroll Cycle Start Date</label>
             <select value={hrSettings.payroll_cycle_start} onChange={(e) => setHrSettings({...hrSettings, payroll_cycle_start: e.target.value})} className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 outline-none">
                <option value="1">1st of the Month</option>
                <option value="21">21st of the Month</option>
                <option value="25">25th of the Month</option>
                <option value="26">26th of the Month</option>
             </select>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mb-6">
            <input type="checkbox" id="geofence" checked={hrSettings.geofence_enabled} onChange={(e) => setHrSettings({...hrSettings, geofence_enabled: e.target.checked})} className="w-5 h-5 text-blue-600 rounded" />
            <label htmlFor="geofence" className="text-sm font-bold text-gray-700">Enable Strict GPS Geofencing (Blocks Clock-In if out of range)</label>
        </div>

        <button onClick={handleSaveConfig} className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-black shadow-sm transition">Save All HR Rules</button>
      </div>

      {/* HOLIDAY CALENDAR */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 border-l-4 border-l-red-500">
        <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-red-500"/> Company Holiday Calendar
        </h3>
        
        <form onSubmit={handleAddHoliday} className="flex gap-4 items-end mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex-1">
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Holiday Name</label>
            <input required type="text" placeholder="e.g., Diwali, Christmas" value={newHoliday.holiday_name} onChange={e => setNewHoliday({...newHoliday, holiday_name: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
            <input required type="date" value={newHoliday.holiday_date} onChange={e => setNewHoliday({...newHoliday, holiday_date: e.target.value})} className="w-full p-2 border border-gray-300 rounded-md" />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <input type="checkbox" checked={newHoliday.is_recurring} onChange={e => setNewHoliday({...newHoliday, is_recurring: e.target.checked})} className="w-4 h-4 rounded" />
            <span className="text-xs font-bold text-gray-600">Yearly</span>
          </div>
          <button type="submit" className="bg-gray-800 text-white px-4 py-2 rounded-md font-bold hover:bg-black"><PlusIcon className="w-5 h-5"/></button>
        </form>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-bold uppercase text-xs">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Holiday Name</th>
                <th className="p-3">Recurring</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {holidays.map(h => (
                <tr key={h.id}>
                  <td className="p-3 font-medium">{new Date(h.holiday_date).toLocaleDateString()}</td>
                  <td className="p-3 text-gray-700">{h.holiday_name}</td>
                  <td className="p-3">{h.is_recurring ? <span className="text-green-600 font-bold text-xs bg-green-50 px-2 py-1 rounded">Yes</span> : 'No'}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => handleDeleteHoliday(h.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><TrashIcon className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
              {holidays.length === 0 && <tr><td colSpan="4" className="p-4 text-center text-gray-400">No holidays added yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- MAIN PAGE ---
export default function SettingsPage() {
  const navigate = useNavigate();
  // Start with modules tab open
  const [activeTab, setActiveTab] = useState('modules');

  // 🟢 UPDATED: Added Notifications to the tabs array
  const tabs = [
    { id: 'modules', label: 'App Modules', icon: SquaresPlusIcon },
    { id: 'custom_fields', label: 'Custom Fields', icon: Squares2X2Icon },
    { id: 'hr_settings', label: 'HR & Attendance', icon: UserGroupIcon },
    { id: 'general', label: 'Appearance & Fonts', icon: ComputerDesktopIcon },
    { id: 'financial', label: 'Financial', icon: CurrencyDollarIcon },
    { id: 'localization', label: 'Language', icon: GlobeAltIcon },
    { id: 'security', label: 'Roles & Permissions', icon: ShieldCheckIcon },
    { id: 'smtp', label: 'Email & SMTP', icon: PaperAirplaneIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon }, // 🟢 NEW TAB
    { id: 'history', label: 'History Logs', icon: ClipboardDocumentListIcon },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen animate-fade-in-up">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-xl font-extrabold text-gray-900 mb-8"><T>System Settings</T></h1>
        
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Tabs */}
          <div className="w-full md:w-64 flex-shrink-0 space-y-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 space-y-6">
            {activeTab === 'modules' && <ModuleSettings />}

            {activeTab === 'custom_fields' && <CustomFieldSettings />}
            {activeTab === 'hr_settings' && <HRSettingsTab />}

            {activeTab === 'general' && (
                <>
                    <ThemeSettings />
                    <FontSettings />
                </>
            )}
            {activeTab === 'financial' && <CurrencySettings />}
            {activeTab === 'localization' && <LanguageSettings />}
            {activeTab === 'history' && <HistoryLogs />}
            
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                            <div className="p-3 bg-emerald-100 text-emerald-600 rounded-lg">
                                <ShieldCheckIcon className="w-8 h-8" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900"><T>Role & Permission Settings</T></h3>
                                <p className="text-gray-500 mt-1 max-w-xl">
                                    Configure global access levels. Toggle permissions for different roles (e.g., Manager, Accountant) to control what features they can see.
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate('/admin/roles/add')}
                            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-emerald-700 transition"
                        >
                            <PlusCircleIcon className="w-5 h-5" /> Create New Role
                        </button>
                    </div>

                    <div className="mt-8 pt-6 border-t border-gray-100 flex gap-4">
                        <button 
                            onClick={() => navigate('/admin/roles')}
                            className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-200 rounded-lg font-bold text-gray-600 hover:border-emerald-500 hover:text-emerald-600 transition"
                        >
                            <UserGroupIcon className="w-5 h-5" /> Manage Existing Roles
                        </button>
                    </div>
                </div>
              </div>
            )}
            {activeTab === 'smtp' && (
                <SMTPSettings />
            )}
            
            {/* 🟢 NEW NOTIFICATIONS RENDER BLOCK */}
            {activeTab === 'notifications' && (
              <div
                className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => navigate('/admin/settings/notifications')}
              >
                <div className="flex items-center gap-4 p-6">
                  <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                    <BellIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      Notification Preferences
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      Choose which alerts you receive, via which channels, and set quiet hours.
                    </p>
                  </div>
                  <svg className="ml-auto w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}